import { useState } from "react";
import { useMembers } from "./useMembers";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/shadcn/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/shadcn/ui/table";
import { Card } from "components/shadcn/ui/card";
import { Button } from "components/shadcn/ui/button";
import { Alert } from "components/shadcn/ui/alert";
import { Loader2, Trash2, X } from "lucide-react";
import clsx from "clsx";
import Loader from "components/common/loader";
import InviteMembers from "./inviteMember";
import { useSelector } from "react-redux";
import { selectCurrentSubscription } from "components/subscription/subscriptionSlice";
import { SUBSCRIPTION_STATUS } from "components/subscription/types";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import { MEMBER_ROLES } from "./types";

export default function Members() {
  const { members, invitations, invite, remove, revokeInvitation, isLoaded } =
    useMembers();

  const currentSubscriptionDetails = useSelector(selectCurrentSubscription);

  const TAB_KEY = "members-active-tab";

  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem(TAB_KEY) ?? "members"
  );

  const onTabChange = (value: string) => {
    setActiveTab(value);
    sessionStorage.setItem(TAB_KEY, value);
  };

  const [showInvite, setShowInvite] = useState(false);
  const [loading, setLoading] = useState(false);

  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const showAlert = (type: "success" | "error", message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 3000);
  };

  if (!isLoaded) {
    return (
      <Card className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </Card>
    );
  }

  const handleInvite = async (emails: string[]) => {
    try {
      setLoading(true);
      await invite(emails);
      setAlert({
        type: "success",
        message: "Invitations sent",
      });
    } catch (error: any) {
      setAlert({
        type: "error",
        message: error?.response?.data?.message || "Failed to send invitations",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (invitation: any) => {
    await invitation.revoke();
    showAlert("success", "Invitation revoked");
    revokeInvitation();
  };

  const InviteSection = (
    <div
      className={clsx(
        "overflow-hidden transition-all duration-300",
        showInvite ? "min-h-60 mt-4" : "max-h-0"
      )}
    >
      <InviteMembers
        open={showInvite}
        loading={loading}
        onCancel={() => setShowInvite(false)}
        onInvite={async (emails) => {
          await handleInvite(emails);
          setShowInvite(false);
        }}
      />
    </div>
  );

  return (
    <Card className="h-full w-full">
      <div className="p-5 space-y-4">
        {alert && (
          <Alert
            variant={alert.type === "success" ? "success" : "destructive"}
            title={alert.message}
          />
        )}
        {loading && <Loader />}

        <Tabs
          value={activeTab}
          onValueChange={onTabChange}
          activationMode="manual"
        >
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="members">
                Members ({members.length}/5)
              </TabsTrigger>
              <TabsTrigger
                value="invitations"
                disabled={
                  currentSubscriptionDetails.status ===
                  SUBSCRIPTION_STATUS.TRAILING
                }
              >
                Invitations ({invitations.length})
              </TabsTrigger>
            </TabsList>

            <Button
              type="button"
              variant="primary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowInvite((v) => !v)}
              disabled={
                currentSubscriptionDetails.status ===
                SUBSCRIPTION_STATUS.TRAILING
              }
            >
              Invite
            </Button>
          </div>

          <TabsContent value="members">
            {InviteSection}
            <Table className="mt-4">
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="text-left">
                    <TableCell>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {member.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{member.joinedAt}</TableCell>
                    <TableCell>{member.role}</TableCell>
                    <TableCell>
                      {member.role === MEMBER_ROLES.MEMBER && (
                        <ToolTipWrapper content="Remove the member from organization">
                          <Button
                            onClick={() => remove(member.userId)}
                            variant="ghost"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </ToolTipWrapper>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="invitations">
            {InviteSection}
            <Table className="mt-4 bg-white">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Invited At</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-sm text-muted-foreground py-3"
                    >
                      No invitations to display
                    </TableCell>
                  </TableRow>
                ) : (
                  invitations.map((invite) => (
                    <TableRow key={invite.id} className="text-left">
                      <TableCell>{invite.emailAddress}</TableCell>
                      <TableCell>
                        {new Date(invite.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {invite.status.charAt(0).toUpperCase() +
                          invite.status.slice(1).toLowerCase()}
                      </TableCell>
                      <TableCell>
                        {invite.status === "pending" && (
                          <ToolTipWrapper content="Revoke">
                            <Button
                              onClick={() => handleRevoke(invite)}
                              variant="ghost"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </ToolTipWrapper>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
}
